<template>
  <AisHierarchicalMenu
    v-bind="$attrs"
    :limit="100"
  >
    <template #default="{ items, refine, createURL }">
      <HierarchicalLinkFilter
        :items="buildDisplayItems(items)"
        :create-url="createURL"
        @item-clicked="handleItemClicked(refine, $event)"
      />
    </template>
  </AisHierarchicalMenu>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { AisHierarchicalMenu } from 'vue-instantsearch/vue3/es';
import { HierarchicalLinkFilter } from '@bc/discovery/ui/filters';
import { overrideLabels } from '../utils/override-labels';
import { handleHierarchicalItemClick } from '../utils/handle-hierarchical-item-click';
import { useCategories as useApiCategories } from '@bc/discovery/domain/catalog';
import { useAisState } from '../composables/use-ais-state';
import type { LabelOverrides } from '../types/label-overrides';
import type { Item } from '@bc/discovery/ui/filters';
import type { SearchCategory } from '@monolith/legacy/types/api/category';

const props = defineProps<{ labelOverrides: LabelOverrides }>();
const emit = defineEmits(['item-clicked']);

const { categories } = useApiCategories();
const { aisState } = useAisState();

const refinedPath = computed<string[]>(() => {
  const hierarchicalRefinements = aisState.refinements.value.hierarchicalFacetsRefinements;
  const firstRefined = Object.values(hierarchicalRefinements).find(
    (values): values is string[] => Array.isArray(values) && values.length > 0,
  );

  return firstRefined?.[0].split(' > ') ?? [];
});

const indexAlgoliaItems = (items: Item[] | null | undefined, accumulator = new Map<string, Item>()): Map<string, Item> => {
  if (!items) {
    return accumulator;
  }

  for (const item of items) {
    accumulator.set(item.value, item);
    indexAlgoliaItems(item.data, accumulator);
  }

  return accumulator;
};

const isPrefixOfRefinedPath = (pathIds: number[]): boolean => {
  const refined = refinedPath.value;
  if (refined.length === 0 || pathIds.length === 0 || pathIds.length > refined.length) {
    return false;
  }

  return pathIds.every((id, index) => String(id) === refined[index]);
};

const isParentOnRefinedPath = (parentPathIds: number[]): boolean => {
  return parentPathIds.length > 0 && isPrefixOfRefinedPath(parentPathIds);
};

const buildNodeFromCategory = (
  category: SearchCategory,
  ancestorIds: number[],
  algoliaIndex: Map<string, Item>,
  allCategories: Record<string, SearchCategory>,
): Item => {
  const pathIds = [...ancestorIds, category.id];
  const hierarchicalValue = pathIds.join(' > ');
  const algoliaItem = algoliaIndex.get(hierarchicalValue);

  const childrenCategories = category.children
    .map((childId) => allCategories[childId])
    .filter((child): child is SearchCategory => Boolean(child));

  const parentIsOnRefinedPath = isParentOnRefinedPath(pathIds);

  const children: Item[] = childrenCategories
    .map((child) => buildNodeFromCategory(child, pathIds, algoliaIndex, allCategories))
    .filter((child) => {
      if (parentIsOnRefinedPath) {
        return true;
      }
      const childPathIds = child.value.split(' > ').map(Number);
      return algoliaIndex.has(child.value) || isPrefixOfRefinedPath(childPathIds);
    });

  return {
    label: category.full_name,
    value: hierarchicalValue,
    count: algoliaItem?.count ?? 0,
    isRefined: algoliaItem?.isRefined ?? isPrefixOfRefinedPath(pathIds),
    data: children.length > 0 ? children : null,
  };
};

const buildDisplayItems = (algoliaItems: Item[]): Item[] => {
  const allCategories = categories.value;
  if (!allCategories || algoliaItems.length === 0) {
    return algoliaItems;
  }

  const algoliaIndex = indexAlgoliaItems(algoliaItems);
  const rootCategories = Object.values(allCategories).filter((cat) => cat.level === 0);

  const enriched = rootCategories
    .map((root) => buildNodeFromCategory(root, [], algoliaIndex, allCategories))
    .filter((node) => {
      const rootId = Number(node.value);
      return algoliaIndex.has(node.value) || isPrefixOfRefinedPath([rootId]);
    });

  overrideLabels(props.labelOverrides, enriched);
  return enriched;
};

const handleItemClicked = (refine: (value: string) => void, item: Item) => {
  emit('item-clicked', item);
  handleHierarchicalItemClick(refine, refinedPath.value, item.value);
};
</script>
